import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from './client'
import { cachedEntryFolderId, entityUnlockHeaders, invalidateLibrary, optimisticEntryPatch, removeCachedEntry } from './entries'
import type { Note, NoteCreate, NoteUpdate } from './types'

export function useNote(id: number | null) {
  const qc = useQueryClient()
  const headers = id != null ? entityUnlockHeaders(qc, 'note', id) : undefined
  return useQuery({
    queryKey: ['notes', id, headers ? 'unlocked' : 'locked'],
    queryFn: async ({ signal }) => {
      const { data } = await http.get<Note>(`/api/notes/${id}`, { signal, headers })
      return data
    },
    enabled: id != null,
  })
}

export function useCreateNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: NoteCreate) => {
      const { data } = await http.post<Note>('/api/notes', body)
      return data
    },
    onSuccess: (note) => {
      qc.setQueriesData<Note>({ queryKey: ['notes', note.id] }, note)
      // Notes have no ['links'] cache presence — entries/tags/folders/counts
      // is the deliberate reach here (see invalidateLibrary).
      invalidateLibrary(qc, { entries: true, tags: true, folders: true, counts: true })
    },
  })
}

export function useUpdateNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: NoteUpdate }) => {
      const { data } = await http.patch<Note>(`/api/notes/${id}`, body, {
        headers: entityUnlockHeaders(qc, 'note', id),
      })
      return data
    },
    onMutate: ({ id, body }) => ({
      previousFolderId: 'folder_id' in body ? cachedEntryFolderId(qc, 'note', id) : undefined,
    }),
    onSuccess: (data, vars, context) => {
      // Freshen the single-note cache in place: a dialog that reopens
      // within staleTime must see the new updated_at, or its next save
      // 409s against the very write it just made.
      qc.setQueriesData<Note>({ queryKey: ['notes', data.id] }, data)
      if (vars.body.tag_ids !== undefined || vars.body.pending_tags !== undefined) {
        qc.invalidateQueries({ queryKey: ['tags'] })
      }
      const folderMoved = 'folder_id' in vars.body && data.folder_id !== context?.previousFolderId
      if (folderMoved) {
        removeCachedEntry(qc, 'note', data.id)
        qc.invalidateQueries({ queryKey: ['folders'] })
      }
      qc.invalidateQueries({ queryKey: ['entries'] })
    },
  })
}

export function useDeleteNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await http.delete(`/api/notes/${id}`, { headers: entityUnlockHeaders(qc, 'note', id) })
    },
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: ['notes', id] })
      invalidateLibrary(qc, { entries: true, tags: true, folders: true, counts: true })
    },
  })
}

// usePinNote shares optimisticEntryPatch with usePinLink/useRefreshPreview;
// notes only exist in the ['entries'] caches, which the helper keys on
// kind === 'note'.
export function usePinNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, pinned }: { id: number; pinned: boolean }) => {
      const { data } = await http.patch<Note>(`/api/notes/${id}`, { pinned }, {
        headers: entityUnlockHeaders(qc, 'note', id),
      })
      return data
    },
    onMutate: async ({ id, pinned }) => optimisticEntryPatch(qc, 'note', id, { pinned }),
    onError: (_err, _vars, ctx) => ctx?.rollback(),
    onSettled: () => invalidateLibrary(qc, { entries: true, folders: true }),
  })
}

export async function uploadNoteImage(file: File): Promise<{ url: string }> {
  const fd = new FormData()
  fd.append('image', file)
  const { data } = await http.post<{ url: string }>('/api/notes/images', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

// Builds the public note page URL. Prefers the slug — mirrors goHref's
// id-or-slug fallback shape for links.
export function goNoteHref(noteOrId: { id: number; slug: string } | number): string {
  if (typeof noteOrId === 'number') return `/n/${noteOrId}`
  return `/n/${noteOrId.slug || noteOrId.id}`
}
